import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { apiService } from '../../../components/Config/api.service';

const CustomReportBuilder = ({ onClose, onSaved, editingReport = null }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [templateId, setTemplateId] = useState(null);
  const [name, setName] = useState('');
  const [docName, setDocName] = useState('');
  const [description, setDescription] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [assignmentId, setAssignmentId] = useState('');
  const [availableFields, setAvailableFields] = useState([]);
  const [columns, setColumns] = useState([]);
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [loadingFields, setLoadingFields] = useState(false);
  const [aggregationType, setAggregationType] = useState('per_payslip');
  const [departments, setDepartments] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const steps = [
    { id: 1, label: t('customReportBuilder.stepInformation'), icon: '📝' },
    { id: 2, label: t('customReportBuilder.stepFilters'), icon: '🔍' },
    { id: 3, label: t('customReportBuilder.stepColumns'), icon: '📊' },
    { id: 4, label: t('customReportBuilder.stepReview'), icon: '✓' }
  ];

  useEffect(() => {
    if (editingReport) {
      const rawId = editingReport.rawId || (typeof editingReport.id === 'string' ? editingReport.id.replace('custom_', '') : editingReport.id);
      setTemplateId(rawId);
      setName(editingReport.label || editingReport.name || '');
      setDocName(editingReport.doc_name || '');
      setDescription(editingReport.description || editingReport.note || '');
      if (editingReport.aggregation_type) setAggregationType(editingReport.aggregation_type);
      if (editingReport.department_id) setDepartmentId(editingReport.department_id);
      if (editingReport.assignment_id) setAssignmentId(editingReport.assignment_id);
      if (editingReport.columns && Array.isArray(editingReport.columns)) {
        setColumns(editingReport.columns.map((c, idx) => ({
          field_code: c.field_code || '',
          label: c.label || c.field_code,
          visible: c.visible !== undefined ? c.visible : true,
          sequence: c.sequence || idx + 1,
          source_model: c.source_model || 'hr.salary.rule',
          model_field_id: c.model_field_id || null,
          salary_rule_id: c.salary_rule_id || null
        })));
      }
    }
  }, [editingReport]);

  useEffect(() => {
    (async () => {
      setLoadingFields(true);
      try {
        const resp = await apiService.getElement('/available-report-fields');
        if (resp?.data?.status === 'success') {
          setAvailableFields(resp.data.data || []);
        }
        const d = await apiService.getElement('/employee/departments');
        if (d?.data?.departments) setDepartments(d.data.departments);
        const a = await apiService.getElement('/employee/assignments');
        if (a?.data?.assignments) setAssignments(a.data.assignments);
      } catch (err) {
        console.error('Erreur récupération données', err);
      } finally {
        setLoadingFields(false);
      }
    })();
  }, []);

  const addedCodes = useMemo(() => new Set(columns.map(c => c.field_code)), [columns]);

  // Filter available fields by search query and not yet added
  const filtered = useMemo(() => {
    const q = (query || '').trim().toLowerCase();
    return availableFields
      .filter(f => {
        if (!f?.code || addedCodes.has(f.code)) return false;
        if (!q) return true;
        return f.code.toLowerCase().includes(q) || (f.label || '').toLowerCase().includes(q);
      })
      .sort((a, b) => {
        // group by source_model then code for predictability
        if (a.source_model === b.source_model) return a.code.localeCompare(b.code);
        return a.source_model.localeCompare(b.source_model);
      });
  }, [availableFields, query, addedCodes]);

  // visible slice (preserve showAll behaviour)
  const visibleFiltered = showAll ? filtered : filtered.slice(0, 40);

  // group visible fields by source_model for better UX
  const groupedFields = useMemo(() => {
    const groups = {};
    for (const f of visibleFiltered) {
      const key = f.source_model || 'autre';
      if (!groups[key]) groups[key] = [];
      groups[key].push(f);
    }
    return groups;
  }, [visibleFiltered]);

  // keep groups collapsed by default; preserve user choices when groups change
  useEffect(() => {
    const keys = Object.keys(groupedFields);
    if (keys.length === 0) return;
    setCollapsedGroups(prev => {
      const next = {};
      keys.forEach(k => {
        next[k] = prev[k] !== undefined ? prev[k] : true; // default collapsed
      });
      return next;
    });
  }, [groupedFields]);

  const toggleGroup = (key) => setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  const expandAll = () => setCollapsedGroups(Object.fromEntries(Object.keys(groupedFields).map(k => [k, false])));
  const collapseAll = () => setCollapsedGroups(Object.fromEntries(Object.keys(groupedFields).map(k => [k, true])));

  // friendly labels for common models
  const modelLabels = {
    'hr.salary.rule': t('customReportBuilder.payrollCodes'),
    'hr.employee': t('customReportBuilder.employeeFields'),
    'hr.contract': t('customReportBuilder.contractFields'),
    'autre': t('customReportBuilder.other')
  };

  const addColumn = (field) => {
    if (!field?.code || addedCodes.has(field.code)) return;
    setColumns(prev => [...prev, { 
      field_code: field.code, 
      label: field.label || field.code, 
      visible: true, 
      sequence: prev.length + 1,
      source_model: field.source_model,
      model_field_id: field.model_field_id || null,
      salary_rule_id: field.salary_rule_id || null
    }]);
  };

  const addColumns = (fields) => {
    setColumns(prev => {
      const next = [...prev];
      for (const f of fields) {
        if (!f?.code || next.some(n => n.field_code === f.code)) continue;
        next.push({
          field_code: f.code,
          label: f.label || f.code,
          visible: true,
          sequence: next.length + 1,
          source_model: f.source_model,
          model_field_id: f.model_field_id || null,
          salary_rule_id: f.salary_rule_id || null
        });
      }
      return next;
    });
  };

  const removeColumn = (index) => {
    setColumns(prev => prev.filter((_, i) => i !== index).map((c, i) => ({ ...c, sequence: i + 1 })));
  };

  const moveColumn = (index, dir) => {
    setColumns(prev => {
      const next = [...prev];
      const swapIndex = index + dir;
      if (swapIndex < 0 || swapIndex >= next.length) return prev;
      [next[swapIndex], next[index]] = [next[index], next[swapIndex]];
      return next.map((c, i) => ({ ...c, sequence: i + 1 }));
    });
  };

  const updateColumn = (index, patch) => {
    setColumns(prev => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return name.trim().length > 0;
      case 2:
        return true; // Filters are optional
      case 3:
        return columns.length > 0;
      case 4:
        return true;
      default:
        return false;
    }
  };

  const save = async () => {
    if (!name.trim()) {
      alert(t('customReportBuilder.nameRequiredAlert'));
      return;
    }
    if (columns.length === 0) {
      alert(t('customReportBuilder.columnRequiredAlert'));
      return;
    }

    const payload = { 
      id: templateId || undefined,
      name, 
      doc_name: docName, 
      note: description,
      aggregation_type: aggregationType,
      department_id: departmentId || undefined,
      assignment_id: assignmentId || undefined,
      columns: columns.map(c => ({ ...c })) 
    };

    try {
      const res = await apiService.saveElement('/custom-report-template', payload);
      if (res?.data) {
        onSaved?.();
        onClose?.();
      } else {
        alert(t('customReportBuilder.saveError'));
      }
    } catch (err) {
      console.error('Erreur sauvegarde', err);
      alert(t('customReportBuilder.saveError'));
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('customReportBuilder.reportName')} *
              </label>
              <input
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[--color-primary] focus:border-transparent"
                placeholder={t('customReportBuilder.reportNamePlaceholder')}
                value={name}
                onChange={e => setName(e.target.value)}
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('customReportBuilder.exportFileName')}
              </label>
              <input
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[--color-primary] focus:border-transparent"
                placeholder={t('customReportBuilder.exportFileNamePlaceholder')}
                value={docName}
                onChange={e => setDocName(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">{t('customReportBuilder.exportFileNameHint')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('absenceForm.description')}
              </label>
              <textarea
                className="w-full border border-gray-300 rounded-lg px-4 py-2 h-32 resize-y focus:ring-2 focus:ring-[--color-primary] focus:border-transparent"
                placeholder={t('customReportBuilder.descriptionPlaceholder')}
                value={description}
                onChange={e => setDescription(e.target.value)}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                ℹ️ {t('customReportBuilder.filtersOptionalHint')}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('customReportBuilder.displayMode')} *
              </label>
              <select
                value={aggregationType}
                onChange={e => setAggregationType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[--color-primary] focus:border-transparent"
              >
                <option value="per_payslip">{t('customReportBuilder.detailedMode')}</option>
                <option value="per_employee">{t('customReportBuilder.aggregatedMode')}</option>
                <option value="per_month">{t('customReportBuilder.monthlyColumnsMode')}</option>
                <option value="total">{t('customReportBuilder.totalMode')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('customReportBuilder.departmentFilter')}
              </label>
              <select
                value={departmentId}
                onChange={e => setDepartmentId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[--color-primary] focus:border-transparent"
              >
                <option value="">{t('customReportBuilder.allDepartments')}</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('customReportBuilder.assignmentFilter')}
              </label>
              <select
                value={assignmentId}
                onChange={e => setAssignmentId(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[--color-primary] focus:border-transparent"
              >
                <option value="">{t('customReportBuilder.allAssignments')}</option>
                {assignments.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                ⚠️ {t('customReportBuilder.selectColumnHint')}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Available Fields */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('customReportBuilder.availableFields')}
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    placeholder={t('customReportBuilder.searchFieldsPlaceholder')}
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Escape') setQuery('');
                    }}
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-[--color-primary] focus:border-transparent"
                  />
                  <button
                    onClick={() => setQuery('')}
                    title={t('customReportBuilder.clear')}
                    className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
                  >
                    {t('customReportBuilder.clear')}
                  </button>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                  <div>
                    {loadingFields ? t('customReportManager.loading') : t('customReportBuilder.fieldsAvailableCount', { count: filtered.length })}
                  </div>
                  {filtered.length > 40 && (
                    <button
                      onClick={() => setShowAll(s => !s)}
                      className="text-[--color-primary] hover:underline"
                    >
                      {showAll ? t('customReportBuilder.showLess') : t('customReportBuilder.showMore', { count: filtered.length - 40 })}
                    </button>
                  )}
                </div>

                <div className="border border-gray-300 rounded-lg h-96 overflow-auto p-2 bg-gray-50">
                  {loadingFields ? (
                    <div className="text-center py-8 text-gray-500">{t('customReportManager.loading')}</div>
                  ) : filtered.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">{t('customReportBuilder.noFieldAvailable')}</div>
                  ) : (
                    <>
                      <div className="flex items-center justify-end gap-2 mb-3">
                        <button onClick={expandAll} className="text-xs px-2 py-1 border rounded">{t('customReportBuilder.expandAll')}</button>
                        <button onClick={collapseAll} className="text-xs px-2 py-1 border rounded">{t('customReportBuilder.collapseAll')}</button>
                      </div>

                      {Object.keys(groupedFields).map(groupKey => {
                        const group = groupedFields[groupKey];
                        const label = modelLabels[groupKey] || groupKey;
                        const groupAvailableCount = group.length;
                        const totalInGroup = filtered.filter(f => (f.source_model || 'autre') === groupKey).length;
                        const isCollapsed = collapsedGroups[groupKey] ?? true;
                        return (
                          <div key={groupKey} className="mb-3">
                            <div role="button" tabIndex={0} className="flex items-center justify-between mb-2 cursor-pointer" onClick={() => toggleGroup(groupKey)} onKeyDown={(e) => { if (e.target !== e.currentTarget) return; if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroup(groupKey); }}} aria-expanded={!isCollapsed}>
                              <div className="text-sm font-semibold flex items-center gap-2">
                                <span className="text-xs">{isCollapsed ? '▶' : '▾'}</span>
                                {label} <span className="text-xs text-gray-400">({totalInGroup})</span>
                              </div>
                              <div className="flex items-center gap-2">
                                {!isCollapsed && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); addColumns(group); }}
                                    disabled={group.every(f => addedCodes.has(f.code))}
                                    className="text-xs px-2 py-1 bg-[--color-primary] text-white rounded hover:bg-[--bg-hover] disabled:opacity-40"
                                    title={t('customReportBuilder.addAllVisibleTitle')}
                                  >
                                    {t('customReportBuilder.addAllCount', { count: groupAvailableCount })}
                                  </button>
                                )}
                              </div>
                            </div>

                            {!isCollapsed && (
                              <div className="space-y-1">
                                {group.map(f => {
                                  const isAdded = addedCodes.has(f.code);
                                  return (
                                    <div
                                      key={f.code}
                                      className="flex items-center justify-between gap-2 p-2 mb-1 bg-white border border-gray-200 rounded hover:border-[--color-primary] transition-colors"
                                    >
                                      <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">{f.label}</div>
                                        <div className="text-xs text-gray-500">{f.code}</div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {isAdded ? (
                                          <div className="text-xs text-green-600 flex items-center gap-1">
                                            <FaCheck /> {t('customReportBuilder.added')}
                                          </div>
                                        ) : (
                                          <button
                                            onClick={() => addColumn(f)}
                                            className="px-3 py-1 bg-[--color-primary] text-white text-xs rounded hover:bg-[--bg-hover] transition-colors flex-shrink-0"
                                          >
                                            {t('customReportBuilder.add')}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>

              {/* Selected Columns */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('customReportBuilder.selectedColumns', { count: columns.length })}
                </label>
                <div className="border border-gray-300 rounded-lg h-[26rem] overflow-auto p-2 bg-white">
                  {columns.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      {t('customReportBuilder.noColumnSelected')}
                    </div>
                  ) : (
                    columns.map((c, i) => (
                      <div
                        key={c.field_code}
                        className="border border-gray-200 rounded-lg p-3 mb-2 bg-gray-50"
                      >
                        <div className="flex items-start gap-2 mb-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">{c.label}</div>
                            <div className="text-xs text-gray-500">{c.field_code}</div>
                          </div>
                          <div className="flex gap-1 flex-shrink-0">
                            <button
                              onClick={() => moveColumn(i, -1)}
                              disabled={i === 0}
                              className="p-1 border border-gray-300 rounded text-xs hover:bg-gray-100 disabled:opacity-30"
                              title={t('customReportBuilder.moveUp')}
                            >
                              ↑
                            </button>
                            <button
                              onClick={() => moveColumn(i, 1)}
                              disabled={i === columns.length - 1}
                              className="p-1 border border-gray-300 rounded text-xs hover:bg-gray-100 disabled:opacity-30"
                              title={t('customReportBuilder.moveDown')}
                            >
                              ↓
                            </button>
                            <button
                              onClick={() => removeColumn(i)}
                              className="p-1 border border-red-300 rounded text-xs text-red-600 hover:bg-red-50"
                              title={t('hspForm.delete')}
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                        <input
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-[--color-primary] focus:border-transparent"
                          value={c.label}
                          onChange={e => updateColumn(i, { label: e.target.value })}
                          placeholder={t('customReportBuilder.columnLabelPlaceholder')}
                        />
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">
                ✓ {t('customReportBuilder.reviewHint')}
              </p>
            </div>

            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">{t('customReportBuilder.generalInformation')}</h4>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-gray-600">{t('absenceType.name')}:</dt>
                    <dd className="font-medium">{name || '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-gray-600">{t('customReportBuilder.file')}:</dt>
                    <dd className="font-medium">{docName || '—'}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-gray-600">{t('absenceForm.description')}:</dt>
                    <dd className="font-medium">{description || '—'}</dd>
                  </div>
                </dl>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">{t('customReportBuilder.stepFilters')}</h4>
                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-gray-600">{t('customReportBuilder.mode')}:</dt>
                    <dd className="font-medium">
                      {aggregationType === 'per_payslip'
                        ? t('customReportBuilder.detailed')
                        : aggregationType === 'per_month'
                        ? t('customReportBuilder.monthlyColumnsMode')
                        : aggregationType === 'total'
                        ? t('customReportBuilder.totalMode')
                        : t('customReportBuilder.aggregatedByEmployee')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-600">{t('customReportBuilder.department')}:</dt>
                    <dd className="font-medium">
                      {departments.find(d => d.id === departmentId)?.name || t('customReportBuilder.all')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-gray-600">{t('customReportBuilder.assignment')}:</dt>
                    <dd className="font-medium">
                      {assignments.find(a => a.id === assignmentId)?.name || t('customReportBuilder.allFeminine')}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">
                  {t('customReportBuilder.stepColumns')} ({columns.length})
                </h4>
                <div className="space-y-1">
                  {columns.map((c, i) => (
                    <div key={c.field_code} className="flex items-center gap-2 text-sm">
                      <span className="text-gray-500">{i + 1}.</span>
                      <span className="font-medium">{c.label}</span>
                      <span className="text-xs text-gray-500">({c.field_code})</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">
              {editingReport ? t('customReportBuilder.editReport') : t('customReportBuilder.createCustomReport')}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            >
              ×
            </button>
          </div>
        </div>

        {/* Steps Progress */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                      currentStep === step.id
                        ? 'bg-[--color-primary] text-white'
                        : currentStep > step.id
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    {currentStep > step.id ? <FaCheck /> : step.icon}
                  </div>
                  <div className="ml-3">
                    <div className="text-sm font-medium text-gray-900">{step.label}</div>
                    <div className="text-xs text-gray-500">{t('customReportBuilder.stepNumber', { number: step.id })}</div>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className="flex-1 h-1 mx-4 bg-gray-200 rounded">
                    <div
                      className={`h-full rounded transition-all ${
                        currentStep > step.id ? 'bg-green-500' : 'bg-gray-200'
                      }`}
                      style={{ width: currentStep > step.id ? '100%' : '0%' }}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={() => setCurrentStep(s => Math.max(1, s - 1))}
            disabled={currentStep === 1}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FaArrowLeft size={14} />
            {t('dailySalaryInputs.previous')}
          </button>

          <div className="text-sm text-gray-600">
            {t('customReportBuilder.stepOf', { current: currentStep, total: steps.length })}
          </div>

          {currentStep < steps.length ? (
            <button
              onClick={() => setCurrentStep(s => s + 1)}
              disabled={!canProceed()}
              className="flex items-center gap-2 px-4 py-2 bg-[--color-primary] text-white rounded-lg hover:bg-[--bg-hover] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {t('dailySalaryInputs.next')}
              <FaArrowRight size={14} />
            </button>
          ) : (
            <button
              onClick={save}
              disabled={!canProceed()}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <FaCheck size={14} />
              {editingReport ? t('customReportBuilder.updateReport') : t('customReportBuilder.createReport')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomReportBuilder;